'use client';

import { Component, type ReactNode } from 'react';

interface ReleaseNoteErrorBoundaryProps {
  readonly children: ReactNode;
  readonly noteId?: string;
}

interface ReleaseNoteErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary for individual release notes
 * Catches rendering errors and displays a fallback UI
 */
export class ReleaseNoteErrorBoundary extends Component<
  ReleaseNoteErrorBoundaryProps,
  ReleaseNoteErrorBoundaryState
> {
  constructor(props: ReleaseNoteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ReleaseNoteErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error rendering release note:', {
      noteId: this.props.noteId,
      error,
      errorInfo,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className='flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3'>
          <div className='flex flex-col gap-1'>
            <div className='font-medium text-destructive'>
              Failed to load release note
            </div>
            <p className='text-xs text-muted-foreground'>
              This release note couldn't be displayed due to invalid data.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
